import type { ComponentDef, ComponentForm, FieldDef, FormModule } from './component-types.js';
import { num } from './component-types.js';
import { MESH_SOURCE_SPEC } from '../../lib/asset-types.js';

export type { ComponentDef, ComponentForm, FieldDef, FieldType } from './component-types.js';

/**
 * Typed form definitions for the scene components documented in
 * engine/ARCHITECTURE.md. Unknown components (and fields of type `json`) fall
 * back to a JSON editor.
 */

export const BUILTIN_SHADERS = ['standard', 'toon', 'unlit'] as const;

/** The shader a material uses (explicit `shader`, else the legacy `toon` flag, else standard). */
export function materialShader(m: Record<string, unknown>): string {
  if (typeof m.shader === 'string' && m.shader) return m.shader;
  if (m.toon === true) return 'toon';
  return 'standard';
}

const isLit = (m: Record<string, unknown>) => materialShader(m) !== 'unlit';
const isToon = (m: Record<string, unknown>) => materialShader(m) === 'toon';
const isCustom = (m: Record<string, unknown>) => materialShader(m).endsWith('.wgsl');
const IMAGE = ['.png', '.jpg', '.jpeg', '.pixel.json'] as const;

/** Toon shader settings (`toonSettings`); defaults = the classic two-band look. */
export const TOON_FIELDS: FieldDef[] = [
  { key: 'threshold', label: 'Threshold', hint: 'N·L where the lit side starts (0)', type: { kind: 'number', step: 0.01, min: -1, max: 1 } },
  { key: 'softness', label: 'Softness', hint: 'terminator width (0.07)', type: { kind: 'number', step: 0.01, min: 0 } },
  { key: 'bandThreshold', label: 'Band threshold', hint: 'second band start (0.45)', type: { kind: 'number', step: 0.01, min: -1, max: 1 } },
  { key: 'bandSoftness', label: 'Band softness', hint: '0.07', type: { kind: 'number', step: 0.01, min: 0 } },
  { key: 'bandWeight', label: 'Band weight', hint: 'share of light in the second band; 0 = one band (0.18)', type: { kind: 'number', step: 0.01, min: 0, max: 1 } },
  { key: 'shadowColor', label: 'Shadow color', hint: 'light kept on the shadow side (black)', type: { kind: 'color', alpha: false } },
  { key: 'specularThreshold', label: 'Specular threshold', hint: 'higher = smaller highlight (0.55)', type: { kind: 'number', step: 0.01, min: 0, max: 1 } },
  { key: 'specularSoftness', label: 'Specular softness', hint: '0.07', type: { kind: 'number', step: 0.01, min: 0 } },
  { key: 'specularStrength', label: 'Specular strength', hint: '0.45', type: { kind: 'number', step: 0.05, min: 0 } },
  { key: 'rimThreshold', label: 'Rim threshold', hint: 'higher = thinner rim (0.62)', type: { kind: 'number', step: 0.01, min: 0, max: 1 } },
  { key: 'rimSoftness', label: 'Rim softness', hint: '0.08', type: { kind: 'number', step: 0.01, min: 0 } },
  { key: 'rimStrength', label: 'Rim strength', hint: '0 = no rim light (0.6)', type: { kind: 'number', step: 0.05, min: 0 } },
  { key: 'rimColor', label: 'Rim color', hint: 'unset = from ambient and main light', type: { kind: 'color', alpha: false } },
  { key: 'ambientFlatten', label: 'Ambient flatten', hint: '0 = follows the normal, 1 = flat (0.5)', type: { kind: 'number', step: 0.05, min: 0, max: 1 } },
  { key: 'metallicDamping', label: 'Metallic damping', hint: 'direct light (0.7)', type: { kind: 'number', step: 0.05, min: 0, max: 1 } },
  { key: 'metallicAmbientDamping', label: 'Metallic ambient damping', hint: '0.5', type: { kind: 'number', step: 0.05, min: 0, max: 1 } },
];


export const PRIMITIVES = ['cube', 'sphere', 'capsule', 'cylinder', 'cone', 'plane', 'torus'] as const;

const BUILTIN_COMPONENTS: ComponentDef[] = [
  {
    key: 'mesh',
    label: 'Mesh',
    icon: 'cube',
    defaults: { source: 'primitive:cube' },
    fields: [
      { key: 'source', label: 'Source', hint: 'A primitive, a model document (model:) or an imported glTF (gltf:)', type: { kind: 'path', ...MESH_SOURCE_SPEC } },
      { key: 'material', label: 'Material', type: { kind: 'path', suffix: '.material.json' } },
      { key: 'castShadows', label: 'Cast shadows', type: { kind: 'bool' } },
      { key: 'cullDistance', label: 'Cull distance (m, 0 = off)', type: num(1, { min: 0 }) },
      {
        key: 'materials',
        label: 'Slot materials',
        hint: 'Override single model material slots, e.g. { "paint": { "color": [1, 0.2, 0.7] } }; other slots keep the model\'s materials. Scripts: e.set_slot_material(slot, map).',
        type: { kind: 'json' },
      },
    ],
  },
  {
    key: 'material',
    label: 'Material',
    icon: 'material',
    defaults: { shader: 'standard', color: [0.8, 0.8, 0.8, 1], metallic: 0, roughness: 0.8 },
    fields: [
      { key: 'shader', label: 'Shader', hint: 'standard (PBR), toon, unlit or a project .wgsl custom shader', type: { kind: 'shader' }, read: materialShader, clears: ['toon'] },
      { key: 'color', label: 'Color', type: { kind: 'color', alpha: true } },
      { key: 'texture', label: 'Texture', type: { kind: 'path', suffix: IMAGE } },
      { key: 'metallic', label: 'Metallic', type: num(0.05, { min: 0, max: 1 }), showIf: isLit },
      { key: 'roughness', label: 'Roughness', type: num(0.05, { min: 0, max: 1 }), showIf: isLit },
      { key: 'emissive', label: 'Emissive', type: { kind: 'color', alpha: false } },
      { key: 'emissiveIntensity', label: 'Emissive intensity', hint: 'multiplies emissive; > 1 blooms', type: num(0.1, { min: 0 }) },
      { key: 'emissiveMap', label: 'Emissive map', hint: 'glow image (sRGB), multiplied by emissive × intensity', type: { kind: 'path', suffix: IMAGE } },
      { key: 'normalMap', label: 'Normal map', type: { kind: 'path', suffix: IMAGE }, showIf: isLit },
      { key: 'normalScale', label: 'Normal scale', type: num(0.05), showIf: isLit },
      {
        key: 'heightMap',
        label: 'Height map',
        type: { kind: 'path', suffix: IMAGE },
        hint: 'White = high. Parallax depth that shifts with the camera (bricks, cobbles, cracks). Uses UVs, not triplanar.',
      },
      { key: 'heightScale', label: 'Height scale', type: num(0.005, { min: 0, max: 0.5 }), read: (v) => v.heightScale ?? 0.05, hint: '0.02 subtle .. 0.15 deep.' },
      { key: 'subsurface', label: 'Subsurface', type: num(0.05, { min: 0, max: 1 }), showIf: isLit, hint: 'Light bleeding under the surface: skin 0.4-0.7, wax, marble, fruit.' },
      { key: 'subsurfaceColor', label: 'Subsurface color', type: { kind: 'color', alpha: false }, showIf: isLit },
      { key: 'translucency', label: 'Translucency', type: num(0.05, { min: 0, max: 4 }), showIf: isLit, hint: 'Light shining through from behind: leaves, petals, grass, ears, paper.' },
      { key: 'translucencyColor', label: 'Translucency color', type: { kind: 'color', alpha: false }, showIf: isLit },
      { key: 'ormMap', label: 'ORM map', hint: 'R occlusion, G roughness, B metallic', type: { kind: 'path', suffix: IMAGE }, showIf: isLit },
      { key: 'metallicRoughnessMap', label: 'Metal/rough map', hint: 'glTF: G roughness, B metallic', type: { kind: 'path', suffix: IMAGE }, showIf: isLit },
      { key: 'occlusionStrength', label: 'Occlusion', type: num(0.05, { min: 0, max: 1 }), showIf: isLit },
      { key: 'alphaCutoff', label: 'Alpha cutoff', hint: 'cut out pixels below this alpha (0 = off)', type: num(0.05, { min: 0, max: 1 }) },
      { key: 'transparent', label: 'Transparent', type: { kind: 'bool' } },
      { key: 'doubleSided', label: 'Double sided', type: { kind: 'bool' } },
      { key: 'uvScale', label: 'UV tiling', hint: '[u, v]', type: { kind: 'json' } },
      { key: 'uvOffset', label: 'UV offset', hint: '[u, v]', type: { kind: 'json' } },
      {
        key: 'textureAnimation',
        label: 'Texture animation',
        hint: 'Flipbook frames of the texture (.pixel.json clip or sprite-sheet grid) and/or scrolling UVs.',
        type: {
          kind: 'object',
          fields: [
            { key: 'clip', label: 'Clip', type: { kind: 'string', placeholder: '.pixel.json animation (empty = first)' } },
            { key: 'columns', label: 'Sheet columns', type: num(1, { min: 0, integer: true }), hint: 'Sprite-sheet image: frames per row (0 = not a sheet).' },
            { key: 'rows', label: 'Sheet rows', type: num(1, { min: 0, integer: true }) },
            { key: 'frames', label: 'Frames used', type: num(1, { min: 0, integer: true }), hint: '0 = all.' },
            { key: 'fps', label: 'FPS', type: num(1, { min: 0 }), hint: '0 = the clip’s own fps (8 for sheets).' },
            { key: 'mode', label: 'Mode', type: { kind: 'enum', options: ['loop', 'pingPong', 'once'] } },
            { key: 'offset', label: 'Time offset (s)', type: num(0.1) },
            { key: 'scroll', label: 'Scroll [u, v]/s', type: { kind: 'json' }, hint: 'Flowing water / lava: e.g. [0.05, 0].' },
          ],
        },
      },
      { key: 'triplanar', label: 'Triplanar', hint: 'project textures from 3 axes (no UVs)', type: { kind: 'bool' } },
      { key: 'triplanarScale', label: 'Triplanar scale', hint: 'meters per texture repeat', type: num(0.1, { min: 0.01 }), showIf: (m) => m.triplanar === true },
      { key: 'toonSettings', label: 'Toon', type: { kind: 'object', fields: TOON_FIELDS }, showIf: isToon },
      { key: 'properties', label: 'Properties', hint: 'custom shader @property values by name', type: { kind: 'json' }, showIf: isCustom },
      { key: 'textures', label: 'Textures', hint: 'custom shader @texture images by name', type: { kind: 'json' }, showIf: isCustom },
    ],
  },
  {
    key: 'light',
    label: 'Light',
    icon: 'light',
    defaults: { kind: 'point', color: [1, 1, 1], intensity: 1, range: 10, shadows: 'none' },
    fields: [
      { key: 'kind', label: 'Type', type: { kind: 'enum', options: ['directional', 'point', 'spot'] } },
      { key: 'color', label: 'Color', type: { kind: 'color', alpha: false } },
      { key: 'intensity', label: 'Intensity', type: num(0.1, { min: 0 }) },
      { key: 'range', label: 'Range', type: num(0.5, { min: 0 }) },
      { key: 'angle', label: 'Spot angle', type: num(1, { min: 0, max: 180 }) },
      { key: 'spotSoftness', label: 'Spot softness', type: num(0.05, { min: 0, max: 1 }) },
      { key: 'shadows', label: 'Shadows', type: { kind: 'enum', options: ['none', 'hard', 'soft'] } },
      { key: 'shadowStrength', label: 'Shadow strength', type: num(0.05, { min: 0, max: 1 }) },
      { key: 'shadowBias', label: 'Shadow bias', type: num(0.05, { min: 0 }) },
      { key: 'shadowNormalBias', label: 'Normal bias', type: num(0.05, { min: 0 }) },
    ],
  },
  {
    key: 'rigidBody',
    label: 'Rigid Body',
    icon: 'sphere',
    defaults: { kind: 'dynamic', mass: 1 },
    fields: [
      { key: 'kind', label: 'Kind', type: { kind: 'enum', options: ['dynamic', 'static', 'kinematic'] } },
      { key: 'isKinematic', label: 'Is kinematic', type: { kind: 'bool' } },
      {
        key: 'interpolation',
        label: 'Interpolation',
        hint: 'smoothing between physics steps: the transform scripts, children and cameras see is smoothed (static bodies never move)',
        type: { kind: 'enum', options: ['interpolate', 'extrapolate', 'none'] },
        read: (v) => v.interpolation ?? 'interpolate',
      },
      { key: 'mass', label: 'Mass (kg)', type: num(0.1, { min: 0 }) },
      { key: 'density', label: 'Density (overrides mass)', type: num(10, { min: 0 }) },
      { key: 'linearDamping', label: 'Drag', type: num(0.05, { min: 0 }) },
      { key: 'angularDamping', label: 'Angular drag', type: num(0.05, { min: 0 }) },
      { key: 'useGravity', label: 'Use gravity', type: { kind: 'bool' } },
      { key: 'gravityScale', label: 'Gravity scale', type: num(0.1) },
      { key: 'ccd', label: 'Continuous collision', type: { kind: 'bool' } },
      { key: 'freezePosition', label: 'Freeze position', type: { kind: 'axes' } },
      { key: 'freezeRotation', label: 'Freeze rotation', type: { kind: 'axes' } },
      { key: 'centerOfMass', label: 'Center of mass', type: { kind: 'vec3' } },
      { key: 'velocity', label: 'Initial velocity', type: { kind: 'vec3' } },
      { key: 'angularVelocity', label: 'Initial angular velocity', type: { kind: 'vec3' } },
      { key: 'friction', label: 'Friction (legacy)', type: num(0.05, { min: 0 }) },
      { key: 'restitution', label: 'Restitution (legacy)', type: num(0.05, { min: 0, max: 1 }) },
    ],
  },
  {
    key: 'collider',
    label: 'Collider',
    icon: 'wireframe',
    defaults: { shape: 'box', size: [1, 1, 1], trigger: false },
    fields: [
      { key: 'shape', label: 'Shape', type: { kind: 'enum', options: ['box', 'sphere', 'capsule', 'mesh', 'terrain'] } },
      { key: 'size', label: 'Size', type: { kind: 'vec3' } },
      { key: 'trigger', label: 'Is trigger', type: { kind: 'bool' } },
      { key: 'offset', label: 'Offset', type: { kind: 'vec3' } },
      { key: 'material', label: 'Physics material', type: { kind: 'path', suffix: '.physicsmat.json', allowObject: true } },
    ],
  },
  {
    key: 'characterController',
    label: 'Character Controller',
    icon: 'team',
    defaults: { height: 1.8, radius: 0.35, speed: 6, jump: 7 },
    fields: [
      { key: 'height', label: 'Height', type: num(0.05, { min: 0 }) },
      { key: 'radius', label: 'Radius', type: num(0.05, { min: 0 }) },
      { key: 'speed', label: 'Speed', type: num(0.1, { min: 0 }) },
      { key: 'jump', label: 'Jump', type: num(0.1, { min: 0 }) },
      {
        key: 'interpolation',
        label: 'Interpolation',
        hint: 'smoothing of the position between physics steps (what scripts, children and cameras see)',
        type: { kind: 'enum', options: ['interpolate', 'extrapolate', 'none'] },
        read: (v) => v.interpolation ?? 'interpolate',
      },
      { key: 'stepHeight', label: 'Step offset', type: num(0.05, { min: 0 }) },
      { key: 'minStepWidth', label: 'Min step width', type: num(0.05, { min: 0 }) },
      { key: 'slopeLimit', label: 'Slope limit (deg)', type: num(1, { min: 0, max: 90 }) },
      { key: 'slideSlope', label: 'Slide slope (deg)', type: num(1, { min: 0, max: 90 }) },
      { key: 'skinWidth', label: 'Skin width', type: num(0.005, { min: 0 }) },
      { key: 'snapToGround', label: 'Snap to ground', type: num(0.05, { min: 0 }) },
      { key: 'useGravity', label: 'Use gravity', type: { kind: 'bool' } },
      { key: 'gravityScale', label: 'Gravity scale', type: num(0.1) },
      { key: 'maxFallSpeed', label: 'Max fall speed (0 = none)', type: num(1, { min: 0 }) },
      { key: 'airJumps', label: 'Air jumps', type: num(1, { min: 0, integer: true }) },
      { key: 'headBump', label: 'Head bump', type: { kind: 'bool' } },
      { key: 'pushRigidbodies', label: 'Push bodies (mass)', type: num(1, { min: 0 }) },
      { key: 'up', label: 'Up', type: { kind: 'vec3' } },
    ],
  },
  {
    key: 'layer',
    label: 'Layer',
    icon: 'link',
    defaults: 'Default',
    scalar: { kind: 'layer' },
    fields: [],
  },
  {
    key: 'sprite',
    label: 'Sprite',
    icon: 'pixel',
    defaults: { sheet: '', animation: '' },
    fields: [
      { key: 'sheet', label: 'Sheet', type: { kind: 'path', suffix: ['.pixel.json', '.sprite.json'] } },
      { key: 'image', label: 'Image', type: { kind: 'path', suffix: ['.png', '.pixel.json'] } },
      { key: 'animation', label: 'Animation', type: { kind: 'string', placeholder: 'idle' } },
      { key: 'frame', label: 'Frame', type: num(1, { min: 0, integer: true }) },
      { key: 'playing', label: 'Playing', type: { kind: 'bool' } },
      { key: 'speed', label: 'Speed', type: num(0.1, { min: 0 }) },
      { key: 'flipX', label: 'Flip X', type: { kind: 'bool' } },
      { key: 'flipY', label: 'Flip Y', type: { kind: 'bool' } },
      { key: 'color', label: 'Tint', type: { kind: 'color', alpha: true } },
      { key: 'layer', label: 'Sorting layer', type: num(1, { integer: true }) },
      { key: 'order', label: 'Order in layer', type: num(1, { integer: true }) },
      { key: 'pivot', label: 'Pivot', type: { kind: 'json' } },
      { key: 'pixelsPerUnit', label: 'Pixels per unit', type: num(1, { min: 1 }) },
      { key: 'visible', label: 'Visible', type: { kind: 'bool' } },
    ],
  },
  {
    key: 'tilemap',
    label: 'Tilemap',
    icon: 'grid',
    defaults: { tileset: '', tileSize: [16, 16], size: [32, 18], layers: [{ name: 'ground', data: [], solid: true }] },
    fields: [
      { key: 'tileset', label: 'Tileset', type: { kind: 'path', suffix: ['.pixel.json', '.png'] } },
      { key: 'tileSize', label: 'Tile size (px)', type: { kind: 'json' } },
      { key: 'solidTiles', label: 'Solid tile ids', type: { kind: 'json' } },
      { key: 'physicsMaterial', label: 'Physics material', type: { kind: 'path', suffix: '.physicsmat.json', allowObject: true } },
      { key: 'layer', label: 'Sorting layer', type: num(1, { integer: true }) },
      { key: 'pixelsPerUnit', label: 'Pixels per unit', type: num(1, { min: 1 }) },
    ],
  },
  {
    key: 'script',
    label: 'Script',
    icon: 'script',
    defaults: { path: 'scripts/new.rhai', props: {} },
    fields: [
      { key: 'path', label: 'Script', type: { kind: 'path', suffix: '.rhai' } },
      {
        key: 'side',
        label: 'Runs on',
        type: { kind: 'enum', options: ['server', 'client', 'both'] },
        hint: 'Multiplayer: server = authoritative gameplay (default), client = UI / effects / camera, both = every machine. Offline runs all of them.',
        read: (v) => v.side ?? 'server',
      },
      { key: 'props', label: 'Props', type: { kind: 'json' } },
    ],
  },
  {
    key: 'sceneStream',
    label: 'Scene Stream',
    icon: 'globe',
    defaults: { scene: 'scenes/area_1.scene.json', loadRadius: 200, unloadRadius: 260 },
    fields: [
      { key: 'scene', label: 'Scene', type: { kind: 'path', suffix: '.scene.json' } },
      { key: 'loadRadius', label: 'Load radius', type: num(5, { min: 0 }) },
      { key: 'unloadRadius', label: 'Unload radius', type: num(5, { min: 0 }) },
      { key: 'editor', label: 'Preview in editor', type: { kind: 'bool' } },
    ],
  },
  {
    key: 'prefab',
    label: 'Prefab',
    icon: 'prefab',
    defaults: { path: 'prefabs/new.prefab.json' },
    fields: [{ key: 'path', label: 'Prefab', type: { kind: 'path', suffix: '.prefab.json' } }],
  },
  {
    key: 'tags',
    label: 'Tags',
    icon: 'link',
    defaults: [],
    array: true,
    fields: [],
  },
  {
    key: 'data',
    label: 'Data',
    icon: 'data',
    defaults: {},
    fields: [],
  },
];

/**
 * Per-system forms: every file in ./forms/ is picked up automatically, so a
 * new engine system adds its Inspector form without editing this file.
 */
const FORM_MODULES = import.meta.glob<FormModule>('./forms/*.ts', { eager: true });

function collectComponents(): ComponentDef[] {
  const entries = new Map<string, { def: ComponentDef; order: number }>();
  BUILTIN_COMPONENTS.forEach((def, i) => entries.set(def.key, { def, order: i * 10 }));
  for (const path of Object.keys(FORM_MODULES).sort()) {
    const exported = FORM_MODULES[path]!.default;
    for (const form of Array.isArray(exported) ? exported : [exported]) {
      const { order, ...def } = form as ComponentForm;
      entries.set(def.key, { def, order: order ?? entries.get(def.key)?.order ?? 1000 });
    }
  }
  return [...entries.values()].sort((a, b) => a.order - b.order).map((e) => e.def);
}

export const COMPONENTS: ComponentDef[] = collectComponents();

export const COMPONENT_BY_KEY = new Map(COMPONENTS.map((c) => [c.key, c]));

/** Starter entities for the hierarchy's create menu. */
export function newEntityTemplate(kind: string): { name: string; components: Record<string, unknown> } {
  if ((PRIMITIVES as readonly string[]).includes(kind)) {
    return {
      name: kind[0]!.toUpperCase() + kind.slice(1),
      components: { mesh: { source: `primitive:${kind}` } },
    };
  }
  switch (kind) {
    case 'pointLight':
      return { name: 'Point Light', components: { light: { kind: 'point', color: [1, 1, 1], intensity: 2, range: 10 } } };
    case 'spotLight':
      return { name: 'Spot Light', components: { light: { kind: 'spot', color: [1, 1, 1], intensity: 3, range: 15, angle: 35 } } };
    case 'directionalLight':
      return { name: 'Directional Light', components: { light: { kind: 'directional', color: [1, 0.96, 0.9], intensity: 2, shadows: 'soft' } } };
    case 'particleSystem':
      return { name: 'Particle System', components: { particleSystem: structuredClone(COMPONENT_BY_KEY.get('particleSystem')?.defaults ?? {}) } };
    case 'water':
      return {
        name: 'Water',
        components: {
          liquid: structuredClone(COMPONENT_BY_KEY.get('liquid')?.defaults ?? { kind: 'water' }),
          material: { color: [0.08, 0.32, 0.42, 0.7], roughness: 0.05, transparent: true, doubleSided: true },
        },
      };
    case 'lava':
      return {
        name: 'Lava',
        components: {
          liquid: {
            kind: 'lava',
            size: [10, 2, 10],
            density: 3100,
            linearDrag: 6,
            damagePerSecond: 40,
            swimming: { enabled: false, speed: 0.3 },
            waves: [{ amplitude: 0.03, wavelength: 2.5, speed: 0.3, direction: [1, 0.2], steepness: 0.5 }],
            underwater: {
              fog: { color: [1, 0.35, 0.05], density: 3 },
              colorAdjustments: { colorFilter: [1, 0.55, 0.25], postExposure: 1 },
              distortion: { strength: 0.012, speed: 0.8 },
              blur: { radius: 4 },
            },
          },
          material: {
            shader: 'unlit',
            color: [1, 0.4, 0.08],
            emissive: [1, 0.35, 0.05],
            emissiveIntensity: 3,
            textureAnimation: { scroll: [0.03, 0.01] },
          },
        },
      };
    case 'decal':
      return {
        name: 'Decal',
        components: {
          decal: structuredClone(COMPONENT_BY_KEY.get('decal')?.defaults ?? { size: [1, 1, 1] }),
          material: { color: [0.05, 0.04, 0.03, 0.85], roughness: 0.95 },
        },
      };
    case 'camera':
      return { name: 'Camera', components: { camera: { fov: 70, near: 0.1, far: 1000, primary: false } } };
    case 'sprite':
      return { name: 'Sprite', components: { sprite: { sheet: '', animation: '' } } };
    case 'tilemap':
      return {
        name: 'Tilemap',
        components: { tilemap: { tileset: '', tileSize: [16, 16], size: [32, 18], layers: [{ name: 'ground', data: [], solid: true }] } },
      };
    default:
      return { name: 'Empty', components: {} };
  }
}
